import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import {
  Box,
  Button,
  Card,
  IconButton,
  Typography
} from "@mui/material";
import { useState } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { uploadFile } from "../../api/file";
import { useErrorHandler } from "../../hooks/Error/useErrorHandler";
import { setLoading } from "../../store/UI";

interface PendingFile {
  id: string;
  file: File;
}

interface FileUploadProps {
  entityId: number;
  managementUnitId?: number;
  captureId?: number;
  flowTestId?: number;
  onFilesUpdated?: () => void;
}

export const FileUpload = ({
  entityId,
  managementUnitId,
  captureId,
  flowTestId,
  onFilesUpdated
}: FileUploadProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const handleError = useErrorHandler();
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);

  const handleDrop = (droppedFiles: File[]) => {
    const filesWithId = droppedFiles.map(file => ({
      id: crypto.randomUUID(),
      file
    }));
    setPendingFiles((prevFiles) => [...prevFiles, ...filesWithId]);
  };

  const handleDeletePendingFile = (id: string) => {
    setPendingFiles(prevFiles => prevFiles.filter(f => f.id !== id));
  };

  const handleUpload = async () => {
    if (pendingFiles.length === 0) return;
    dispatch(setLoading(true));
    try {
      await Promise.all(
        pendingFiles.map((item) =>
          uploadFile({
            entityId,
            managementUnitId,
            captureId,
            flowTestId,
            file: item.file,
          })
        )
      );
      setPendingFiles([]);
      onFilesUpdated?.();
    } catch (error) {
      handleError(error);
    }
    dispatch(setLoading(false));
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    accept: {
        'image/*': ['.jpeg', '.jpg', '.png'],
        'application/pdf': ['.pdf'],
        'application/msword': ['.doc'],
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
        'application/vnd.ms-excel': ['.xls'],
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
        'application/vnd.ms-powerpoint': ['.ppt'],
        'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
        'application/zip': ['.zip'],
        'application/x-tar': ['.tar'],
        'application/gzip': ['.gz'],
        'application/x-7z-compressed': ['.7z'],
    }, 
  });

  return (
    <>
      <Box sx={{ display: "flex", gap: 3, mb: 2 }}>
        <Box
          {...getRootProps()}
          sx={{
            border: isDragActive ? "2px solid gray" : "2px dashed gray",
            borderRadius: 1,
            p: 3,
            textAlign: "center",
            width: "100%",
            cursor: "pointer",
          }}
        >
          <input {...getInputProps()} />
          <CloudUploadIcon sx={{ fontSize: 60, color: "gray", mb: 1 }} />
          <Typography>
            {isDragActive
              ? t("generic.files.drop-here")
              : t("generic.files.drag-or-click")
            }
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          mt: 2,
          mb: 3,
          maxHeight: 250,
          overflowY: "auto",
          pr: 1
        }}
      >
        {pendingFiles.map((item) => (
          <Card
            key={item.id}
            variant="outlined"
            sx={{
              width: "calc(33.33% - 8px)",
              minWidth: 200,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <InsertDriveFileIcon sx={{ color: "gray" }} />
              <Typography variant="body2">{item.file.name}</Typography>
            </Box>
            <IconButton
              onClick={() => handleDeletePendingFile(item.id)}
              color="error"
              size="small"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Card>
        ))}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end"}}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleUpload}
          disabled={pendingFiles.length === 0}
          sx={{ px: 4, py: 1}}
        >
          {t("generic.files.upload")}
        </Button>
      </Box>
    </>
  );
};